import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Calendar, dateFnsLocalizer, Event } from 'react-big-calendar';
import {
  format,
  parse,
  startOfWeek,
  getDay,
  addDays,
  isSameDay,
  getHours,
} from 'date-fns';
import { uk } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { SleepCalendarFact } from '@/services/sleep.service';
import { styled } from '@mui/material/styles';

import { CalendarEventDto } from '@/types/calendar';
import { DateRangeView } from '@components/common/CalendarToolbar';
import { CalendarViewerContext } from '@components/calendar/CalendarWidget';
import { CalendarEventCard } from './CalendarEventCard';
import { EventCardStatus } from './CalendarEventStatusCard';
import { WorkingHoursDto } from '@/types/appointment.types';

// React Big Calendar custom views
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import Week from 'react-big-calendar/lib/Week';

const locales = { uk };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Custom View for 2 Weeks (standard calendar)
function TwoWeekView({ date, localizer, ...props }: any) {
  const range = useMemo(() => {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 14 }, (_, i) => addDays(start, i));
  }, [date]);

  return <TimeGrid {...props} range={range} eventOffset={15} localizer={localizer} />;
}

TwoWeekView.range = (date: Date) => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 14 }, (_, i) => addDays(start, i));
};

TwoWeekView.navigate = (date: Date, action: 'PREV' | 'NEXT' | 'TODAY') => {
  switch (action) {
    case 'PREV':
      return addDays(date, -14);
    case 'NEXT':
      return addDays(date, 14);
    default:
      return date;
  }
};

TwoWeekView.title = (date: Date) => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = addDays(start, 13);
  return `${format(start, 'd MMM', { locale: uk })} - ${format(end, 'd MMM yyyy', { locale: uk })}`;
};

export interface ClientCalendarEventsProps {
  events: CalendarEventDto[];
  loading: boolean;
  error: string | null;
  currentDate: Date;
  dateView: DateRangeView;
  viewMode: 'list' | 'calendar';
  clientCalendars: { id: number; name: string; isDefault: boolean }[];
  viewerContext?: CalendarViewerContext;
  workingHours?: WorkingHoursDto[];
  onEventClick: (eventId: number) => void;
  onEdit?: (event: CalendarEventDto) => void;
  onDelete?: (event: CalendarEventDto) => void;
  onCellAddEvent?: (date: Date, anchor: HTMLElement) => void;
  onCellQuickAddEvent?: (date: Date, anchor: HTMLElement) => void;
  onEventDrop?: (eventId: number, newDate: Date, copy: boolean) => void;
  /** Факти сну ("Ліг" / "Встав") - лише перегляд, без кліку й перетягування (2026-09-30). */
  sleepFacts?: SleepCalendarFact[];
  /** Додатковий вміст заголовка дня поруч із датою; `compact` - вузькі колонки (подання "2 тижні"). */
  renderDayHeaderExtra?: (day: Date, compact: boolean) => React.ReactNode;
  /** Другий рядок заголовка дня, під датою. */
  renderDayHeaderSummary?: (day: Date, compact: boolean) => React.ReactNode;
  /** Позначка виконання на картці події; задано - картки в новому вигляді (див. `EventCardStatus`). */
  getEventStatus?: (event: CalendarEventDto) => EventCardStatus;
}

interface CalendarEvent extends Event {
  id: number;
  eventTypeId: number;
  eventTypeName: string;
  isCompleted: boolean;
  details: any;
  calendarId: number;
  calendarName?: string;
}

const StyledCalendar = styled(Box)(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  '& .rbc-calendar': {
    flex: 1,
    minHeight: '500px',
    width: '100%',
  },
  '& .rbc-toolbar': {
    display: 'none',
  },
}));

const TableContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  width: '100%',
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  '&[data-dragging="true"] .cell-add-btn': {
    opacity: '0 !important',
    transition: 'none !important',
    pointerEvents: 'none',
  },
}));

const StyledTable = styled('table')(() => ({
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: '100%',
  minWidth: '800px',
  tableLayout: 'fixed',
}));

const StyledTh = styled('th')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 10,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(0.5),
  fontWeight: 500,
  fontSize: '0.75rem',
  textAlign: 'center',
  verticalAlign: 'top',
  height: '36px',
  boxSizing: 'border-box',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StickyCornerTh = styled(StyledTh)(() => ({
  left: 0,
  zIndex: 11,
  width: '60px',
  minWidth: '60px',
}));

const TimeCellTd = styled('td')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  left: 0,
  zIndex: 5,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  width: '60px',
  minWidth: '60px',
  textAlign: 'center',
  verticalAlign: 'top',
  padding: '4px',
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  boxSizing: 'border-box',
}));

const ContentTd = styled('td')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  verticalAlign: 'top',
  padding: '2px',
  boxSizing: 'border-box',
  position: 'relative',
  '& .cell-actions': {
    opacity: 0,
    transform: 'translate(-50%, -50%) scale(0.6)',
    transition: 'opacity 0s, transform 0s',
    position: 'absolute',
    top: '50%',
    left: '50%',
    zIndex: 2,
    display: 'flex',
    gap: '4px',
    pointerEvents: 'none',
    '@media (hover: none)': {
      display: 'none',
    },
  },
  '&:hover .cell-actions': {
    opacity: 1,
    transform: 'translate(-50%, -50%) scale(1)',
    transition: 'opacity 0.15s ease 0.18s, transform 0.15s ease 0.18s',
    pointerEvents: 'auto',
  },
  '&[data-dragover="true"]': {
    backgroundColor: theme.palette.action.selected,
    outline: `2px dashed ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
}));

const SlotContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%',
  height: '100%',
});

export const ClientCalendarEvents: React.FC<ClientCalendarEventsProps> = ({
  events,
  loading,
  error,
  currentDate,
  dateView,
  viewMode,
  clientCalendars,
  viewerContext,
  workingHours,
  onEventClick,
  onEdit,
  onDelete,
  onCellAddEvent,
  onCellQuickAddEvent,
  onEventDrop,
  sleepFacts = [],
  renderDayHeaderExtra,
  renderDayHeaderSummary,
  getEventStatus,
}) => {
  const draggedEventIdRef = useRef<number | null>(null);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isCopyKeyRef = useRef(false);

  // TODO: Ctrl+drag (copy) не працює на Mac — браузер не передає metaKey під час drop.
  //       Перевірити поведінку на Windows (там Ctrl має працювати).
  //       Можливо, потрібен інший UX для копіювання (наприклад, окрема кнопка або контекстне меню).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') isCopyKeyRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') isCopyKeyRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const CALENDAR_COLORS = [
    '#1565c0',
    '#2e7d32',
    '#e65100',
    '#6a1b9a',
    '#00695c',
    '#c62828',
    '#4527a0',
    '#f9a825',
  ];

  const nonDefaultCalendars = clientCalendars.filter(c => !c.isDefault);

  const getCalendarName = (calendarId: number): string | undefined => {
    const calendar = clientCalendars.find(cal => cal.id === calendarId);
    if (!calendar || calendar.isDefault) return undefined;
    return calendar.name;
  };

  const getCalendarAccentColor = (calendarId: number): string | undefined => {
    const idx = nonDefaultCalendars.findIndex(c => c.id === calendarId);
    if (idx === -1) return undefined;
    return CALENDAR_COLORS[idx % CALENDAR_COLORS.length];
  };

  const isWorkingHour = (day: Date, hour: number) => {
    if (!workingHours || workingHours.length === 0) return false;

    // Convert date-fns day (0=Sun) to WorkingHoursDto day (1=Mon, ..., 7=Sun)
    const dayOfWeek = getDay(day) === 0 ? 7 : getDay(day);

    const schedule = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);
    if (!schedule || !schedule.isActive) return false;

    const [startH, startM] = schedule.startTime.split(':').map(Number);
    const [endH, endM] = schedule.endTime.split(':').map(Number);

    const slotStart = hour * 60;
    const slotEnd = (hour + 1) * 60;
    const workingStart = startH * 60 + startM;
    const workingEnd = endH * 60 + endM;

    // Slot is within working hours if it overlaps or starts at the boundary
    return slotStart <= workingEnd && slotEnd > workingStart;
  };

  const renderCustomGrid = () => {
    let days: Date[] = [];
    if (dateView === 'day') {
      days = [currentDate];
    } else if (dateView === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    } else if (dateView === '2weeks') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      days = Array.from({ length: 14 }, (_, i) => addDays(start, i));
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }

    const startHour = 0;
    const endHour = 23;
    const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => i + startHour);

    return (
      <TableContainer data-dragging={isDragging ? 'true' : undefined}>
        <StyledTable>
          <thead>
            <tr>
              <StickyCornerTh />
              {days.map(day => (
                <StyledTh key={day.toISOString()}>
                  {format(day, 'EEE, d MMM', { locale: uk })}
                  {renderDayHeaderExtra?.(day, dateView === '2weeks')}
                  {renderDayHeaderSummary?.(day, dateView === '2weeks')}
                </StyledTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map(hour => (
              <tr key={hour}>
                <TimeCellTd>{`${hour.toString().padStart(2, '0')}:00`}</TimeCellTd>
                {days.map(day => {
                  const slotEvents = events.filter(event => {
                    if (!event.scheduledLdt) return false;
                    const eventDate = new Date(event.scheduledLdt);
                    return isSameDay(eventDate, day) && getHours(eventDate) === hour;
                  });

                  const slotSleepFacts = sleepFacts.filter(fact => isSameDay(fact.at, day) && getHours(fact.at) === hour);

                  const cellKey = `${day.toISOString()}-${hour}`;
                  const isWorking = isWorkingHour(day, hour);

                  return (
                    <ContentTd
                      key={cellKey}
                      data-dragover={dragOverCell === cellKey ? 'true' : undefined}
                      sx={{
                        backgroundColor: isWorking ? 'rgba(156, 39, 176, 0.08)' : 'inherit',
                      }}
                      onClick={e => {
                        // On touch devices, single tap on empty area triggers AI quick add
                        if (window.matchMedia('(hover: none)').matches && onCellQuickAddEvent) {
                          const cellDate = new Date(
                            day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0,
                          );
                          onCellQuickAddEvent(cellDate, e.currentTarget);
                        }
                      }}
                      onDragOver={onEventDrop ? e => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = isCopyKeyRef.current ? 'copy' : 'move';
                        setDragOverCell(cellKey);
                      } : undefined}
                      onDragLeave={onEventDrop ? () => setDragOverCell(null) : undefined}
                      onDrop={onEventDrop ? e => {
                        e.preventDefault();
                        setDragOverCell(null);
                        const eventId = draggedEventIdRef.current;
                        if (eventId == null) return;
                        const cellDate = new Date(
                          day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0,
                        );
                        onEventDrop(eventId, cellDate, isCopyKeyRef.current);
                      } : undefined}
                    >
                      <SlotContent>
                        {slotSleepFacts.map(fact => (
                          // Факт сну - лише перегляд: редагує тільки клієнт у застосунку.
                          <Box
                            key={`sleep-${fact.sessionId}-${fact.type}`}
                            onClick={e => e.stopPropagation()}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25, mb: 0.5,
                              borderRadius: 1, bgcolor: 'rgba(63, 135, 245, 0.10)', color: '#2F6FD6', fontSize: '0.75rem',
                            }}
                            title={fact.type === 'bed' ? 'Сон: ліг (відмітка клієнта)' : 'Сон: встав (відмітка клієнта)'}
                          >
                            {fact.type === 'bed' ? <BedtimeIcon sx={{ fontSize: 14 }} /> : <WbSunnyIcon sx={{ fontSize: 14 }} />}
                            <span>{fact.type === 'bed' ? 'Ліг' : 'Встав'} {format(fact.at, 'HH:mm')}</span>
                          </Box>
                        ))}
                        {slotEvents.map(event => {
                          const isOwnEvent = viewerContext && event.createdBy?.id === viewerContext.userId;
                          const isDraggable = !!onEventDrop && event.eventType.code !== 'appointment' && (!viewerContext || !!isOwnEvent);
                          return (
                            <Box
                              key={event.id}
                              draggable={isDraggable}
                              onClick={e => e.stopPropagation()} // Prevent triggering cell click on mobile
                              onDragStart={isDraggable ? e => {
                                draggedEventIdRef.current = event.id;
                                e.dataTransfer.effectAllowed = 'copyMove';
                                setIsDragging(true);
                              } : undefined}
                              onDragEnd={isDraggable ? () => {
                                draggedEventIdRef.current = null;
                                setDragOverCell(null);
                                setIsDragging(false);
                              } : undefined}
                              sx={{ cursor: isDraggable ? 'grab' : 'default' }}
                            >
                              <CalendarEventCard
                                event={event}
                                calendarName={getCalendarName(event.calendarId)}
                                calendarAccentColor={getCalendarAccentColor(event.calendarId)}
                                currentUserId={viewerContext?.userId}
                                completionStatus={getEventStatus?.(event)}
                                onClick={() => onEventClick(event.id)}
                                onEdit={isOwnEvent && onEdit ? () => onEdit(event) : undefined}
                                onDelete={isOwnEvent && onDelete ? () => onDelete(event) : undefined}
                              />
                            </Box>
                          );
                        })}
                      </SlotContent>
                      <Box className="cell-actions">
                        {onCellQuickAddEvent && (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              const cellDate = new Date(
                                day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0,
                              );
                              onCellQuickAddEvent(cellDate, e.currentTarget);
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0,
                              bgcolor: '#ff8f00',
                              color: 'common.white',
                              '&:hover': { bgcolor: '#e65100' },
                            }}
                            title="Швидке додавання (AI)"
                          >
                            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        {onCellAddEvent && (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              const cellDate = new Date(
                                day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0,
                              );
                              onCellAddEvent(cellDate, e.currentTarget);
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0,
                              bgcolor: 'success.main',
                              color: 'common.white',
                              '&:hover': { bgcolor: 'success.dark' },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </Box>
                    </ContentTd>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>
    );
  };

  if (loading && events.length === 0) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (viewMode === 'list') {
    return renderCustomGrid();
  }

  const rbcView = dateView === '2weeks' ? 'twoWeeks' : dateView;

  const calendarEvents: CalendarEvent[] = events.map(event => {
    const eventTypeName = event.eventType.name;
    return {
      id: event.id,
      title: event.title || eventTypeName,
      start: event.scheduledLdt ? new Date(event.scheduledLdt) : new Date(),
      end: event.scheduledLdt ? new Date(event.scheduledLdt) : new Date(),
      resource: {
        eventTypeId: event.eventType.id,
        eventTypeName,
        color: event.eventType.color,
        isCompleted: event.isCompleted,
        details: event.details,
        calendarId: event.calendarId,
      },
      eventTypeId: event.eventType.id,
      eventTypeName,
      isCompleted: event.isCompleted,
      details: event.details,
      calendarId: event.calendarId,
      calendarName: clientCalendars.find(c => c.id === event.calendarId)?.name,
    };
  });

  const eventStyleGetter = (event: CalendarEvent) => {
    const backgroundColor = event.resource?.color || '#607D8B';
    return {
      style: {
        backgroundColor,
        opacity: event.isCompleted ? 0.6 : 1,
        textDecoration: event.isCompleted ? 'line-through' : 'none',
        fontSize: '0.8rem',
      },
    };
  };

  return (
    <StyledCalendar>
      <Calendar
        localizer={localizer}
        culture="uk"
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        eventPropGetter={eventStyleGetter}
        views={{
          day: true,
          week: Week,
          twoWeeks: TwoWeekView,
          month: true,
        } as any}
        view={rbcView as any}
        onView={() => {}}
        date={currentDate}
        onNavigate={() => {}}
        onSelectEvent={(event: CalendarEvent) => onEventClick(event.id)}
        step={60}
        timeslots={1}
        min={new Date(0, 0, 0, 6, 0, 0)}
        scrollToTime={new Date(0, 0, 0, 8, 0, 0)}
        components={{
          toolbar: () => null,
          event: ({ event }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
              <span style={{ marginRight: 4 }}>{event.title}</span>
            </Box>
          ),
        }}
      />
    </StyledCalendar>
  );
};
